import type { CSSProperties, ImgHTMLAttributes } from "react";
import {
  useWardrobeImageUrl,
  withRenderTransform,
  type WardrobeItemRef,
} from "@/lib/wardrobeImageUrl";

interface BaseProps {
  item:
    | Pick<WardrobeItemRef, "id" | "image_url" | "image_path" | "cutout_url" | "cutout_status">
    | null
    | undefined;
  /** When provided, applies the Supabase render transform for resizing. */
  transformWidth?: number;
  transformQuality?: number;
}

/**
 * <img> that auto-resolves a fresh signed URL for a wardrobe item.
 * Renders nothing when the item has no resolvable image.
 */
export function WardrobeImg({
  item,
  transformWidth,
  transformQuality,
  alt = "",
  ...imgProps
}: BaseProps & Omit<ImgHTMLAttributes<HTMLImageElement>, "src">) {
  const resolved = useWardrobeImageUrl(item ?? null);
  if (!resolved) return null;
  const src = transformWidth
    ? (withRenderTransform(resolved, transformWidth, transformQuality) ?? resolved)
    : resolved;
  return <img loading="lazy" decoding="async" src={src} alt={alt} {...imgProps} />;
}

/**
 * <div> with backgroundImage auto-resolved from a wardrobe item. Useful for
 * tile layouts that already paint a colour swatch underneath.
 */
export function WardrobeBgTile({
  item,
  transformWidth,
  transformQuality,
  style,
  children,
  ...rest
}: BaseProps & React.HTMLAttributes<HTMLDivElement>) {
  const resolved = useWardrobeImageUrl(item ?? null);
  const src = resolved
    ? transformWidth
      ? (withRenderTransform(resolved, transformWidth, transformQuality) ?? resolved)
      : resolved
    : null;
  const merged: CSSProperties = {
    ...style,
    backgroundImage: src ? `url(${src})` : style?.backgroundImage,
  };
  return (
    <div style={merged} {...rest}>
      {children}
    </div>
  );
}

/**
 * <button> tile variant — same as WardrobeBgTile but renders a button so it
 * can be used inline in click-to-select grids.
 */
export function WardrobeBgButton({
  item,
  transformWidth,
  transformQuality,
  style,
  children,
  ...rest
}: BaseProps & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const resolved = useWardrobeImageUrl(item ?? null);
  const src = resolved
    ? transformWidth
      ? (withRenderTransform(resolved, transformWidth, transformQuality) ?? resolved)
      : resolved
    : null;
  const merged: CSSProperties = {
    ...style,
    backgroundImage: src ? `url(${src})` : style?.backgroundImage,
  };
  return (
    <button style={merged} {...rest}>
      {children}
    </button>
  );
}

